(() => {
  const BUTTON_LABELS = {
    0: 'Left',
    1: 'Middle',
    2: 'Right',
    3: 'Back',
    4: 'Forward'
  };

  const KEY_NAME_LABELS = {
    ' ': 'Space',
    ArrowDown: 'Down',
    ArrowLeft: 'Left',
    ArrowRight: 'Right',
    ArrowUp: 'Up',
    Control: 'Ctrl',
    Escape: 'Esc',
    Meta: 'Win',
    OS: 'Win'
  };

  const formatMs = (value) => `${Math.round(value)} ms`;
  const formatNumber = (value, digits = 0) => Number(value || 0).toFixed(digits);
  const formatClock = (date = new Date()) => `${date.toLocaleTimeString([], { hour12: false })}.${String(date.getMilliseconds()).padStart(3, '0')}`;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const median = (values) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  };

  class MouseKeyboardTester {
    constructor(root) {
      this.root = root;
      this.$ = (selector, scope = this.root) => scope.querySelector(selector);
      this.$$ = (selector, scope = this.root) => Array.from(scope.querySelectorAll(selector));

      this.clickState = {
        total: 0,
        doubleCount: 0,
        rapidCount: 0,
        lastAt: 0,
        timestamps: [],
        counts: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 },
        releaseCounts: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 },
        active: new Set(),
        pointer: { x: null, y: null },
        log: []
      };

      this.dpiState = {
        active: false,
        distancePx: 0,
        samples: 0
      };

      this.pollState = {
        active: false,
        startedAt: 0,
        lastAt: 0,
        intervals: [],
        raf: 0,
        timer: 0
      };

      this.scrollState = {
        events: 0,
        total: 0,
        lastDirection: '-'
      };

      this.reactionState = {
        waiting: false,
        ready: false,
        signalAt: 0,
        timer: 0
      };

      this.keyState = {
        active: new Set(),
        triggered: new Set(),
        events: 0,
        log: []
      };

      this.traceState = {
        drawing: false,
        lastPoint: null,
        points: 0,
        distance: 0,
        peakSpeed: 0
      };

      this.init();
    }

    init() {
      this.initTabs();
      this.initClickTest();
      this.initDpiTest();
      this.initPollingTest();
      this.initScrollTest();
      this.initLatencyTest();
      this.initMacroTest();
      this.initTraceCanvas();
    }

    setText(selector, value) {
      this.$$(selector).forEach((el) => {
        el.textContent = value;
      });
    }

    initTabs() {
      const tabs = this.$$('[data-mkt-tab]');
      const panels = this.$$('[data-mkt-tab-panel]');
      if (!tabs.length || !panels.length) return;

      const activate = (target) => {
        tabs.forEach((tab) => {
          const isActive = tab.dataset.mktTab === target;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.mktTabPanel === target;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        if (target === 'trace') {
          requestAnimationFrame(() => this.traceResize?.());
        }
      };

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => activate(tab.dataset.mktTab));
      });

      activate(tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.mktTab || tabs[0].dataset.mktTab);
    }

    initClickTest() {
      const zone = this.$('[data-click-zone]');
      if (!zone) return;

      const shouldGuardSideButton = (event) => {
        const isSideButton = event.button === 3 || event.button === 4;
        return isSideButton && (this.mouseZoneActive || zone.contains(event.target));
      };

      this.mouseZoneActive = false;
      zone.addEventListener('contextmenu', (event) => event.preventDefault());
      zone.addEventListener('mouseenter', () => {
        this.mouseZoneActive = true;
      });
      zone.addEventListener('mouseleave', () => {
        this.mouseZoneActive = false;
        this.clickState.pointer = { x: null, y: null };
        this.renderPointerPosition();
      });
      zone.addEventListener('focusin', () => {
        this.mouseZoneActive = true;
      });
      zone.addEventListener('focusout', () => {
        this.mouseZoneActive = false;
      });

      ['mousedown', 'mouseup', 'auxclick'].forEach((eventName) => {
        document.addEventListener(eventName, (event) => {
          if (!shouldGuardSideButton(event)) return;
          event.preventDefault();
          if (eventName === 'auxclick') event.stopPropagation();
        }, { capture: true, passive: false });
      });

      ['mousedown', 'mouseup', 'auxclick'].forEach((eventName) => {
        zone.addEventListener(eventName, (event) => {
          event.preventDefault();
          if (eventName === 'auxclick') event.stopPropagation();
        }, { passive: false });
      });
      zone.addEventListener('mousedown', (event) => {
        event.preventDefault();
        zone.focus({ preventScroll: true });
        this.updatePointerPosition(event, zone);
        this.recordMouseButton('down', event.button);
        zone.classList.add('is-hit');
        window.setTimeout(() => zone.classList.remove('is-hit'), 140);
      });
      zone.addEventListener('mouseup', (event) => {
        event.preventDefault();
        this.updatePointerPosition(event, zone);
        this.recordMouseButton('up', event.button);
      });
      zone.addEventListener('mousemove', (event) => this.updatePointerPosition(event, zone));

      this.$('[data-click-reset]')?.addEventListener('click', () => {
        this.clickState = {
          total: 0,
          doubleCount: 0,
          rapidCount: 0,
          lastAt: 0,
          timestamps: [],
          counts: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 },
          releaseCounts: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 },
          active: new Set(),
          pointer: { x: null, y: null },
          log: []
        };
        this.renderClickStats('-', '-');
        this.renderPointerPosition();
        this.renderMouseButtonVisuals();
        this.renderMouseLog();
      });

      window.setInterval(() => this.renderCps(), 250);
    }

    updatePointerPosition(event, zone) {
      const rect = zone.getBoundingClientRect();
      this.clickState.pointer = {
        x: Math.round(clamp(event.clientX - rect.left, 0, rect.width)),
        y: Math.round(clamp(event.clientY - rect.top, 0, rect.height))
      };
      this.renderPointerPosition();
    }

    renderPointerPosition() {
      const { x, y } = this.clickState.pointer || {};
      this.setText('[data-pointer-position]', Number.isFinite(x) && Number.isFinite(y) ? `X ${x} / Y ${y}` : 'X - / Y -');
    }

    recordMouseButton(type, button) {
      const normalizedButton = Object.prototype.hasOwnProperty.call(BUTTON_LABELS, button) ? button : 0;
      const now = performance.now();
      const interval = this.clickState.lastAt ? now - this.clickState.lastAt : 0;

      if (type === 'down') {
        this.clickState.total += 1;
        this.clickState.counts[normalizedButton] = (this.clickState.counts[normalizedButton] || 0) + 1;
        this.clickState.timestamps.push(now);
        this.clickState.active.add(normalizedButton);

        if (interval > 0 && interval <= 250) this.clickState.doubleCount += 1;
        if (interval > 0 && interval <= 120) this.clickState.rapidCount += 1;

        this.clickState.lastAt = now;
        this.renderClickStats(BUTTON_LABELS[normalizedButton], interval ? formatMs(interval) : '-');
      } else {
        this.clickState.releaseCounts[normalizedButton] = (this.clickState.releaseCounts[normalizedButton] || 0) + 1;
        this.clickState.active.delete(normalizedButton);
        this.renderClickStats(`${BUTTON_LABELS[normalizedButton]} release`, this.$('[data-click-interval]')?.textContent || '-');
      }

      this.clickState.log.unshift({
        type,
        button: BUTTON_LABELS[normalizedButton],
        time: formatClock()
      });
      this.clickState.log = this.clickState.log.slice(0, 16);
      this.renderMouseButtonVisuals();
      this.renderMouseLog();
    }

    renderMouseButtonVisuals() {
      Object.keys(BUTTON_LABELS).forEach((button) => {
        const isActive = this.clickState.active.has(Number(button));
        this.$$(`[data-button-state="${button}"], [data-button-visual="${button}"]`).forEach((target) => {
          target.classList.toggle('is-active', isActive);
        });
      });
    }

    renderClickStats(lastButton, intervalText) {
      this.setText('[data-click-total]', String(this.clickState.total));
      this.setText('[data-last-button]', lastButton);
      this.setText('[data-click-interval]', intervalText);
      this.setText('[data-double-count]', String(this.clickState.doubleCount));
      this.setText('[data-rapid-count]', String(this.clickState.rapidCount));

      Object.keys(BUTTON_LABELS).forEach((button) => {
        this.setText(`[data-button-down-count="${button}"]`, String(this.clickState.counts[button] || 0));
        this.setText(`[data-button-up-count="${button}"]`, String(this.clickState.releaseCounts[button] || 0));
      });

      this.renderCps();
    }

    renderMouseLog() {
      this.$$('[data-mouse-log]').forEach((log) => {
        log.innerHTML = this.clickState.log.map((item) => (
          `<li><strong class="mkt-event-log__key">${this.escapeHtml(item.button)}</strong><span class="mkt-event-log__type">${item.type}</span><time class="mkt-event-log__time">${item.time}</time></li>`
        )).join('');
      });
    }

    renderCps() {
      const now = performance.now();
      this.clickState.timestamps = this.clickState.timestamps.filter((time) => now - time <= 5000);
      const oneSecondClicks = this.clickState.timestamps.filter((time) => now - time <= 1000).length;
      this.setText('[data-cps]', formatNumber(oneSecondClicks, 1));
    }

    initDpiTest() {
      const pad = this.$('[data-dpi-pad]');
      if (!pad) return;

      const setPadText = (state) => {
        const key = state === 'active' ? 'dpiActiveText' : state === 'stopped' ? 'dpiStoppedText' : 'dpiIdleText';
        pad.textContent = pad.dataset[key] || pad.textContent;
      };

      const handleMove = (event) => {
        if (!this.dpiState.active) return;
        const dx = event.movementX || 0;
        const dy = event.movementY || 0;
        const distance = Math.hypot(dx, dy);
        if (!distance) return;
        this.dpiState.distancePx += distance;
        this.dpiState.samples += 1;
        this.renderDpi();
      };

      document.addEventListener('mousemove', handleMove);
      document.addEventListener('pointerlockchange', () => {
        if (document.pointerLockElement !== pad && this.dpiState.active) {
          this.stopDpiCapture('Released');
        }
      });

      document.addEventListener('pointerlockerror', () => {
        if (!this.dpiState.active) return;
        this.dpiState.active = false;
        pad.classList.remove('is-active');
        setPadText('idle');
        this.setText('[data-dpi-status]', 'Pointer lock blocked');
      });

      document.addEventListener('keydown', (event) => {
        if (!this.dpiState.active) return;
        if (!['Escape', 'Enter', ' '].includes(event.key)) return;
        if (event.key !== 'Escape') event.preventDefault();
        this.stopDpiCapture(event.key === 'Escape' ? 'Released' : 'Stopped');
      });

      this.$('[data-dpi-start]')?.addEventListener('click', () => {
        this.dpiState.active = true;
        pad.classList.add('is-active');
        this.setText('[data-dpi-status]', 'Capturing');
        setPadText('active');
        pad.focus({ preventScroll: true });

        if (pad.requestPointerLock) {
          pad.requestPointerLock();
        } else {
          this.setText('[data-dpi-status]', 'Pointer lock unavailable');
        }
      });

      this.$('[data-dpi-stop]')?.addEventListener('click', () => this.stopDpiCapture('Stopped'));
      this.$('[data-dpi-reset]')?.addEventListener('click', () => {
        this.dpiState.distancePx = 0;
        this.dpiState.samples = 0;
        this.stopDpiCapture('Idle');
        setPadText('idle');
        this.renderDpi();
      });

      this.updateDpiPadText = setPadText;
    }

    stopDpiCapture(status) {
      this.dpiState.active = false;
      this.$('[data-dpi-pad]')?.classList.remove('is-active');
      this.setText('[data-dpi-status]', status);
      this.updateDpiPadText?.(status === 'Idle' ? 'idle' : 'stopped');
      if (document.pointerLockElement && document.exitPointerLock) {
        document.exitPointerLock();
      }
    }

    renderDpi() {
      const distanceInput = this.$('[data-dpi-distance]');
      const unitInput = this.$('[data-dpi-unit]');
      const distanceValue = Math.max(0.1, Number(distanceInput?.value || 10));
      const unit = unitInput?.value || 'cm';
      const inches = unit === 'cm' ? distanceValue / 2.54 : distanceValue;
      const estimate = inches > 0 ? this.dpiState.distancePx / inches : 0;

      this.setText('[data-dpi-pixels]', `${Math.round(this.dpiState.distancePx)} px`);
      this.setText('[data-dpi-estimate]', estimate > 0 ? `${Math.round(estimate)} effective DPI` : '-');
    }

    initPollingTest() {
      const pad = this.$('[data-poll-pad]');
      if (!pad) return;

      pad.addEventListener('pointermove', () => {
        if (!this.pollState.active) return;
        const now = performance.now();
        if (this.pollState.lastAt) {
          const interval = now - this.pollState.lastAt;
          if (interval > 0 && interval < 250) this.pollState.intervals.push(interval);
        }
        this.pollState.lastAt = now;
        this.setText('[data-poll-samples]', String(this.pollState.intervals.length));
      });

      this.$('[data-poll-start]')?.addEventListener('click', () => this.startPollingTest());
    }

    startPollingTest() {
      const pad = this.$('[data-poll-pad]');
      const progress = this.$('[data-poll-progress]');
      if (!pad) return;

      window.clearTimeout(this.pollState.timer);
      cancelAnimationFrame(this.pollState.raf);

      this.pollState.active = true;
      this.pollState.startedAt = performance.now();
      this.pollState.lastAt = 0;
      this.pollState.intervals = [];
      pad.classList.add('is-active');
      pad.focus({ preventScroll: true });
      this.setText('[data-poll-hz]', 'Move now');
      this.setText('[data-poll-samples]', '0');
      this.setText('[data-poll-interval]', '-');
      if (progress) progress.style.width = '0%';

      const tick = () => {
        const elapsed = performance.now() - this.pollState.startedAt;
        const percent = clamp(elapsed / 5000 * 100, 0, 100);
        if (progress) progress.style.width = `${percent}%`;
        if (elapsed < 5000 && this.pollState.active) {
          this.pollState.raf = requestAnimationFrame(tick);
        }
      };

      tick();
      this.pollState.timer = window.setTimeout(() => this.finishPollingTest(), 5000);
    }

    finishPollingTest() {
      const pad = this.$('[data-poll-pad]');
      const intervals = this.pollState.intervals;
      const medianInterval = median(intervals);
      const hz = medianInterval > 0 ? 1000 / medianInterval : 0;

      this.pollState.active = false;
      pad?.classList.remove('is-active');
      this.setText('[data-poll-hz]', hz ? `${Math.round(hz)} Hz` : 'Not enough movement');
      this.setText('[data-poll-interval]', medianInterval ? `${formatNumber(medianInterval, 1)} ms` : '-');
      this.setText('[data-poll-samples]', String(intervals.length));
    }

    initScrollTest() {
      const pad = this.$('[data-scroll-pad]');
      if (!pad) return;

      pad.addEventListener('wheel', (event) => {
        event.preventDefault();
        this.scrollState.events += 1;
        this.scrollState.total += event.deltaY;
        this.scrollState.lastDirection = event.deltaY > 0 ? 'Down' : 'Up';
        pad.classList.add('is-active');
        window.clearTimeout(this.scrollState.timeout);
        this.scrollState.timeout = window.setTimeout(() => pad.classList.remove('is-active'), 160);
        this.renderScroll();
      }, { passive: false });

      this.$('[data-scroll-reset]')?.addEventListener('click', () => {
        this.scrollState.events = 0;
        this.scrollState.total = 0;
        this.scrollState.lastDirection = '-';
        this.renderScroll();
      });
    }

    renderScroll() {
      const average = this.scrollState.events ? this.scrollState.total / this.scrollState.events : 0;
      this.setText('[data-scroll-events]', String(this.scrollState.events));
      this.setText('[data-scroll-total]', formatNumber(this.scrollState.total, 0));
      this.setText('[data-scroll-average]', this.scrollState.events ? formatNumber(average, 1) : '-');
      this.setText('[data-scroll-direction]', this.scrollState.lastDirection);
    }

    initLatencyTest() {
      const latencyPad = this.$('[data-latency-pad]');
      const reactionPad = this.$('[data-reaction-pad]');

      latencyPad?.addEventListener('pointerdown', () => {
        const startedAt = performance.now();
        latencyPad.classList.add('is-active');
        requestAnimationFrame(() => {
          this.setText('[data-latency-frame]', formatMs(performance.now() - startedAt));
          window.setTimeout(() => latencyPad.classList.remove('is-active'), 140);
        });
      });

      reactionPad?.addEventListener('click', () => this.handleReactionClick());
    }

    handleReactionClick() {
      const pad = this.$('[data-reaction-pad]');
      if (!pad) return;

      if (this.reactionState.ready) {
        const reaction = performance.now() - this.reactionState.signalAt;
        this.reactionState.ready = false;
        pad.classList.remove('is-ready');
        pad.textContent = 'Start reaction drill';
        this.setText('[data-reaction-time]', formatMs(reaction));
        this.setText('[data-reaction-status]', 'Captured');
        return;
      }

      if (this.reactionState.waiting) {
        window.clearTimeout(this.reactionState.timer);
        this.reactionState.waiting = false;
        pad.classList.remove('is-waiting');
        pad.textContent = 'Start reaction drill';
        this.setText('[data-reaction-status]', 'Too early');
        return;
      }

      this.reactionState.waiting = true;
      pad.classList.add('is-waiting');
      pad.textContent = 'Wait for orange...';
      this.setText('[data-reaction-status]', 'Waiting');

      const delay = 900 + Math.random() * 1800;
      this.reactionState.timer = window.setTimeout(() => {
        this.reactionState.waiting = false;
        this.reactionState.ready = true;
        this.reactionState.signalAt = performance.now();
        pad.classList.remove('is-waiting');
        pad.classList.add('is-ready');
        pad.textContent = 'Click now';
        this.setText('[data-reaction-status]', 'Click now');
      }, delay);
    }

    initMacroTest() {
      const pad = this.$('[data-key-pad]');
      const zone = this.$('[data-key-zone]') || pad;
      if (!pad) return;

      const activateKeyboardZone = () => {
        this.keyboardZoneActive = true;
        pad.classList.add('is-active');
      };

      const deactivateKeyboardZone = () => {
        this.keyboardZoneActive = false;
        pad.classList.remove('is-active');
        this.keyState.active.clear();
        this.renderKeyboardVisuals();
        this.renderKeyStats();
      };

      const shouldCaptureKeyboard = (event) => {
        if (this.dpiState.active) return false;
        return this.keyboardZoneActive || zone.contains(document.activeElement) || zone.contains(event.target);
      };

      const stopKeyboardDefault = (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
      };

      const handleKeyDown = (event) => {
        if (!shouldCaptureKeyboard(event)) return;
        stopKeyboardDefault(event);
        activateKeyboardZone();
        const code = event.code || event.key;
        this.keyState.active.add(code);
        this.keyState.triggered.add(code);
        this.recordKeyEvent('down', event);
        this.renderKeyboardVisuals();
      };

      const handleKeyUp = (event) => {
        if (!shouldCaptureKeyboard(event)) return;
        stopKeyboardDefault(event);
        const code = event.code || event.key;
        this.keyState.active.delete(code);
        this.recordKeyEvent('up', event);
        this.renderKeyboardVisuals();
      };

      zone.addEventListener('contextmenu', (event) => event.preventDefault());
      zone.addEventListener('mouseenter', activateKeyboardZone);
      zone.addEventListener('mouseleave', () => {
        if (!zone.contains(document.activeElement)) deactivateKeyboardZone();
      });
      zone.addEventListener('pointerdown', (event) => {
        if (this.isKeyboardInteractiveTarget(event.target)) return;
        activateKeyboardZone();
        pad.focus({ preventScroll: true });
      });
      zone.addEventListener('focusin', activateKeyboardZone);
      zone.addEventListener('focusout', () => {
        window.setTimeout(() => {
          if (!zone.contains(document.activeElement) && !zone.matches(':hover')) deactivateKeyboardZone();
        }, 0);
      });

      document.addEventListener('pointerdown', (event) => {
        if (!zone.contains(event.target)) deactivateKeyboardZone();
      }, { capture: true });

      document.addEventListener('keydown', handleKeyDown, { capture: true });
      document.addEventListener('keyup', handleKeyUp, { capture: true });

      this.$$('[data-focus-keyboard-map]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          pad.focus({ preventScroll: false });
        });
      });

      this.$$('[data-macro-reset]').forEach((button) => button.addEventListener('click', () => {
        this.keyState.active.clear();
        this.keyState.triggered.clear();
        this.keyState.events = 0;
        this.keyState.log = [];
        this.renderKeyboardVisuals();
        this.renderKeyStats();
        this.renderKeyLog();
      }));
    }

    isKeyboardInteractiveTarget(target) {
      return Boolean(target?.closest?.('button, a, input, textarea, select, summary, [contenteditable="true"]'));
    }

    recordKeyEvent(type, event) {
      this.keyState.events += 1;
      const combo = this.describeKeyCombo(event);
      this.keyState.log.unshift({
        type,
        combo,
        repeat: event.repeat,
        time: formatClock()
      });
      this.keyState.log = this.keyState.log.slice(0, 24);
      this.setText('[data-last-key]', combo);
      this.renderKeyStats();
      this.renderKeyLog();
    }

    describeKeyCombo(event) {
      const parts = [];
      if (event.ctrlKey && event.key !== 'Control') parts.push('Ctrl');
      if (event.shiftKey && event.key !== 'Shift') parts.push('Shift');
      if (event.altKey && event.key !== 'Alt') parts.push('Alt');
      if (event.metaKey && !['Meta', 'OS'].includes(event.key)) parts.push('Win');
      parts.push(KEY_NAME_LABELS[event.key] || event.key);
      return parts.join(' + ');
    }

    renderKeyStats() {
      this.setText('[data-active-keys]', String(this.keyState.active.size));
      this.setText('[data-key-events]', String(this.keyState.events));
      if (!this.keyState.events) this.setText('[data-last-key]', '-');
    }

    renderKeyboardVisuals() {
      this.$$('[data-key-code]').forEach((key) => {
        key.classList.toggle('is-active', this.keyState.active.has(key.dataset.keyCode));
        key.classList.toggle('was-triggered', this.keyState.triggered.has(key.dataset.keyCode));
      });
    }

    renderKeyLog() {
      this.$$('[data-key-log]').forEach((log) => {
        log.innerHTML = this.keyState.log.map((item) => {
          const type = item.repeat ? 'repeat' : `key ${item.type}`;
          return `<li><strong class="mkt-event-log__key">${this.escapeHtml(item.combo)}</strong><span class="mkt-event-log__type">${type}</span><time class="mkt-event-log__time">${item.time}</time></li>`;
        }).join('');
      });
    }

    escapeHtml(value) {
      return String(value).replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      })[char]);
    }

    initTraceCanvas() {
      const canvas = this.$('[data-trace-canvas]');
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const resize = () => {
        const rect = canvas.getBoundingClientRect();
        const ratio = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(rect.width * ratio));
        canvas.height = Math.max(1, Math.round(rect.height * ratio));
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        this.drawTraceBackground(ctx, rect.width, rect.height);
      };

      this.traceResize = resize;
      resize();
      window.addEventListener('resize', resize);

      canvas.addEventListener('pointerdown', (event) => {
        const point = this.getCanvasPoint(canvas, event);
        this.traceState.drawing = true;
        this.traceState.lastPoint = { ...point, time: performance.now() };
        canvas.setPointerCapture?.(event.pointerId);
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!this.traceState.drawing) return;
        const point = this.getCanvasPoint(canvas, event);
        this.drawTraceSegment(ctx, this.traceState.lastPoint, point);
        this.traceState.lastPoint = { ...point, time: performance.now() };
        this.renderTraceStats();
      });

      canvas.addEventListener('pointerup', (event) => {
        this.traceState.drawing = false;
        canvas.releasePointerCapture?.(event.pointerId);
      });

      canvas.addEventListener('pointerleave', () => {
        this.traceState.drawing = false;
      });

      this.$('[data-trace-reset]')?.addEventListener('click', () => {
        this.traceState.drawing = false;
        this.traceState.lastPoint = null;
        this.traceState.points = 0;
        this.traceState.distance = 0;
        this.traceState.peakSpeed = 0;
        resize();
        this.renderTraceStats();
      });
    }

    drawTraceBackground(ctx, width, height) {
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.018)';
      const gap = 28;
      for (let x = 0; x <= width; x += gap) {
        ctx.fillRect(x, 0, 1, height);
      }
      for (let y = 0; y <= height; y += gap) {
        ctx.fillRect(0, y, width, 1);
      }
      ctx.fillStyle = 'rgba(255, 106, 26, 0.32)';
      ctx.font = '700 12px sans-serif';
      ctx.fillText('Hold and move to draw trajectory', 18, 26);
    }

    getCanvasPoint(canvas, event) {
      const rect = canvas.getBoundingClientRect();
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    }

    drawTraceSegment(ctx, from, to) {
      if (!from || !to) return;
      const now = performance.now();
      const distance = Math.hypot(to.x - from.x, to.y - from.y);
      const elapsed = Math.max(1, now - from.time);
      const speed = distance / elapsed * 1000;
      const speedRatio = clamp(speed / 2400, 0, 1);

      this.traceState.points += 1;
      this.traceState.distance += distance;
      this.traceState.peakSpeed = Math.max(this.traceState.peakSpeed, speed);

      ctx.beginPath();
      ctx.moveTo(from.x, from.y);
      ctx.lineTo(to.x, to.y);
      ctx.lineWidth = 2 + speedRatio * 4;
      ctx.lineCap = 'round';
      ctx.strokeStyle = `rgba(255, ${Math.round(106 + speedRatio * 95)}, ${Math.round(26 + speedRatio * 90)}, ${0.64 + speedRatio * 0.32})`;
      ctx.shadowColor = 'rgba(255, 106, 26, 0.32)';
      ctx.shadowBlur = 8;
      ctx.stroke();
      ctx.shadowBlur = 0;
    }

    renderTraceStats() {
      this.setText('[data-trace-points]', String(this.traceState.points));
      this.setText('[data-trace-distance]', `${Math.round(this.traceState.distance)} px`);
      this.setText('[data-trace-speed]', `${Math.round(this.traceState.peakSpeed)} px/s`);
    }
  }

  document.querySelectorAll('[data-mkt-tool]').forEach((root) => {
    new MouseKeyboardTester(root);
  });
})();
